import React from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { Link } from 'react-router-dom';
import { PrimaryButton } from './PrimaryButton';

/**
 * ErrorBoundary - React Error Boundary pro zachycení runtime chyb
 * Použití: Obalit aplikaci nebo kritické sekce
 * 
 * @example
 * <ErrorBoundary>
 *   <App />
 * </ErrorBoundary>
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null,
      errorInfo: null 
    };
  }

  static getDerivedStateFromError(error) {
    // Aktualizovat state, aby se zobrazil fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Logovat chybu do error tracking služby
    console.error('Error caught by ErrorBoundary:', error, errorInfo);
    
    // Odeslat do Sentry (pokud je nakonfigurován)
    try {
      const { captureException } = require('../../config/sentry');
      captureException(error, {
        component: 'ErrorBoundary',
        componentStack: errorInfo.componentStack,
        errorBoundary: true,
      });
    } catch (e) {
      // Sentry není dostupný, ignorujeme
    }

    this.setState({
      error,
      errorInfo
    });
  }

  handleReset = () => {
    this.setState({ 
      hasError: false, 
      error: null,
      errorInfo: null 
    });
  }

  render() {
    if (this.state.hasError) {
      // Fallback UI
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
          <div className="max-w-2xl w-full bg-white section-rounded p-8 md:p-12 shadow-lg text-center">
            <div className="w-16 h-16 bg-red-100 section-rounded flex items-center justify-center mx-auto mb-6">
              <AlertTriangle className="w-8 h-8 text-red-600" />
            </div>
            
            <h1 className="text-2xl md:text-3xl font-bold text-text-primary mb-4">
              Něco se pokazilo
            </h1>
            
            <p className="text-body text-text-secondary mb-6 leading-relaxed">
              Omlouváme se za nepříjemnosti. Došlo k neočekávané chybě. 
              Zkuste prosím obnovit stránku nebo se vraťte na hlavní stránku.
            </p>

            {process.env.NODE_ENV === 'development' && this.state.error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 section-rounded text-left">
                <details className="cursor-pointer">
                  <summary className="font-semibold text-red-800 mb-2">
                    Detaily chyby (pouze ve vývoji)
                  </summary>
                  <pre className="text-xs text-red-700 mt-2 overflow-auto max-h-64">
                    {this.state.error.toString()}
                    {this.state.errorInfo?.componentStack}
                  </pre>
                </details>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <PrimaryButton
                onClick={this.handleReset}
                size="md"
                className="w-full sm:w-auto"
              >
                <RefreshCw className="w-5 h-5 mr-2" />
                Zkusit znovu
              </PrimaryButton>
              
              <Link to="/">
                <PrimaryButton
                  size="md"
                  className="w-full sm:w-auto"
                >
                  <Home className="w-5 h-5 mr-2" />
                  Domů
                </PrimaryButton>
              </Link>
            </div>

            <div className="mt-8 pt-6 border-t border-gray-200">
              <p className="text-sm text-text-secondary mb-2">
                Pokud problém přetrvává, kontaktujte nás prosím:
              </p>
              <a 
                href="mailto:info@nis2ochrana.cz" 
                className="text-brand-primary hover:text-brand-primaryDark font-semibold"
              >
                info@nis2ochrana.cz
              </a>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;

